import { breakpointsTailwind, useBreakpoints as useBreakpoint } from '@vueuse/core';
import dayjs from 'dayjs';
import { FlattenObjectKeys } from 'src/types/overlays';

export function useBreakpoints() {
  const breakpoints = useBreakpoint(breakpointsTailwind);

  const isTablet = breakpoints.between('sm', 'lg');
  const isMobile = breakpoints.smaller('md');
  const isDesktop = breakpoints.greater('md');

  return {
    isTablet,
    isMobile,
    isDesktop,
  };
}

export function capitalise(string: string) {
  return string?.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()) || '';
}

export function getFullName(firstName: string | undefined, lastName: string | undefined) {
  if (firstName && lastName) {
    return `${capitalise(firstName)} ${capitalise(lastName)}`;
  }
  return firstName || lastName || 'Unknown';
}

export function validateEmail(email?: string) {
  if (!email) return true;
  return Boolean(
    String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
  );
}

// eslint-disable-next-line
export function checkDeeplyNestedObjectProperties<T extends Record<string, any>>(
  object: T,
  requiredFields?: FlattenObjectKeys<T> | FlattenObjectKeys<T>[] | undefined
): boolean {
  if (!requiredFields) return true;

  if (Array.isArray(requiredFields)) {
    return requiredFields.every((field) => checkDeeplyNestedObjectProperties(object, field));
  }

  const fields = requiredFields.split('.');
  let currentObject = object;
  for (const field of fields) {
    if (!currentObject[field]) return false;
    currentObject = currentObject[field];
  }
  return true;
}

export function isOpenHours(hours: Array<{ open?: string; close?: string }> | undefined, integrationTimezone?: string) {
  if (!hours) return true;
  // days format is 0 for Sunday, 1 for Monday, etc.
  // transform it to 0 for Monday, 1 for Tuesday, etc.
  const currentDayIndex =
    (Number(
      dayjs()
        .tz(integrationTimezone || 'Europe/London')
        .format('d')
    ) +
      6) %
    7;
  const currentDay = hours[currentDayIndex];

  if (currentDay.open && currentDay.close) {
    const open = dayjs(currentDay.open, 'HH:mm');
    const close = dayjs(currentDay.close, 'HH:mm');
    return dayjs()
      .tz(integrationTimezone || 'Europe/London')
      .isBetween(open, close, null, '[]');
  }
  return false;
}

export function validatePostcode(postcode?: string) {
  if (!postcode) return true;
  return postcode.match(/^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i);
}

/**
  Loads a script dynamically
  @param src - The URL of the script to load
  @param options -
  @returns A Promise that resolves when the script loads
*/
export function loadScript(
  src: string,
  options?: {
    attributes?: Record<string, string | boolean>;
    id?: string;
    additionalScript?: string;
  }
) {
  return new Promise((resolve, reject) => {
    if (options?.additionalScript) {
      const addScript = document.createElement('script');
      addScript.innerText = options.additionalScript;
      document.body.appendChild(addScript);
    }

    const s = document.createElement('script');

    s.setAttribute('src', src);
    if (options?.id) {
      s.setAttribute('id', `widget-appended-${options.id}`);
    } else {
      s.setAttribute('id', `widget-appended-${new Date().getTime()}`);
    }
    if (options?.attributes) {
      Object.entries(options.attributes).forEach(([key, value]) => {
        s.setAttribute(key, String(value));
      });
    }
    s.addEventListener('load', resolve);
    s.addEventListener('error', reject);

    document.body.appendChild(s);
  });
}

export async function initializeScript(
  scriptName: string,
  scriptSrc: string,
  beforeLoad: (scriptName: string) => void
): Promise<void> {
  // Execute the beforeLoad function if provided
  if (beforeLoad) {
    beforeLoad(scriptName);
  }

  // Create the script tag
  const tag = document.createElement('script');
  tag.async = true;
  tag.src = scriptSrc;

  // Insert the script tag into the document
  const firstScriptTag = document.getElementsByTagName('script')[0];

  // Return a promise that resolves when the script is loaded
  return new Promise((resolve, reject) => {
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error(`Failed to load script: ${scriptSrc}`));
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  });
}

export function getUtmParams() {
  const urlParams = new URLSearchParams(location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  return keys.reduce(
    (acc, key) => {
      if (urlParams.has(key)) acc[key] = urlParams.get(key);
      return acc;
    },
    {} as Record<string, string | null>
  );
}

export async function wait(time: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
}
